import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from 'environments/environment';
import {
  AppUpdateService,
  AuthInterceptor,
  AuthService,
  StateService,
} from '@cosmos/angular/utils';
import { initConfig } from './initConfig';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { CosmosPreset } from './app-preset';
import { DatePipe, DecimalPipe } from '@angular/common';
import { provideFormlyCore } from '@ngx-formly/core';
import { withFormlyPrimeNG } from '@ngx-formly/primeng';
import {
  FormlyChipsNumberComponent,
  InputTextareaCosmos,
  InputTextFieldCosmos,
  MultiSelectFieldCosmos,
  PrimeNGCheckboxWrapper,
  PrimeNGDefaultWrapper,
  PrimeNGFloatLabelWrapper,
  SelectFieldCosmos,
} from '@cosmos/angular/ui';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

export const appConfig: ApplicationConfig = {
  providers: [
    // ROUTER
    provideRouter(routes),
    // SERVICE WORKER
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production, // Disable in development, enable in production
    }),
    // auto-reload as soon as a new SW version finishes downloading, so open
    // tabs don't get stuck running a stale build (see AppUpdateService)
    provideAppInitializer(() => inject(AppUpdateService).init()),
    // INTERNATIONALIZATION
    provideTranslateService({
      loader: provideTranslateHttpLoader(),
      fallbackLang: 'es_ES',
      lang: 'es_ES',
    }),
    // HTTP — CSRF header injection is handled in AuthInterceptor (cross-origin compatible)
    provideHttpClient(
      withInterceptorsFromDi(),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // DYNAMIC FORMS
    provideFormlyCore([
      // ...withFormlyMaterial(),
      ...withFormlyPrimeNG(),
      {
        types: [
          {
            name: 'chips-number',
            wrappers: ['form-field'],
            component: FormlyChipsNumberComponent,
            defaultOptions: { defaultValue: [] },
          },
          { name: 'input', component: InputTextFieldCosmos },
          { name: 'textarea', component: InputTextareaCosmos },
          { name: 'select', component: SelectFieldCosmos },
          { name: 'multiselect', component: MultiSelectFieldCosmos },
        ],
        wrappers: [
          { name: 'float-label-wrapper', component: PrimeNGFloatLabelWrapper },
          { name: 'default-wrapper', component: PrimeNGDefaultWrapper },
          { name: 'checkbox-wrapper', component: PrimeNGCheckboxWrapper },
        ],
      },
    ]),
    // INITIALIZERS
    // auth — unconditionally calls GET /users/current; browser sends cosmos_session cookie automatically
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const stateService = inject(StateService);

      return firstValueFrom(authService.updateCurrentUser())
        .then(() => { stateService.markInitialized(); return true; })
        .catch((error) => {
          console.error('[AppInit] Failed to fetch user:', error);
          authService.purgeAuth();
          stateService.markInitialized();
          return false;
        });
    }),
    // config
    provideAppInitializer(initConfig()),
    // monitoring

    // STYLES
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: CosmosPreset,
        options: { darkModeSelector: '.dark' },
      },
    }),

    // PIPES
    DatePipe,
    DecimalPipe,
    // PRIMENG GLOBAL SERVICES
    MessageService,
  ],
};
